import { createFileRoute } from "@tanstack/react-router";
import { Button, Chip, Spinner, Table } from "@heroui/react";

import { useOrdersStatistics } from "@/features/order/use-orders.ts";
import { orderTools } from "@/shared/helper/order.ts";
import {
  OrderStatisticsPeriod,
  OrderStatus,
  OrderStatusStatistic,
} from "@/features/order/types.ts";

type StatisticsSearch = {
  period: OrderStatisticsPeriod;
};

const PERIOD_KEYS: OrderStatisticsPeriod[] = [
  "today",
  "threeDays",
  "week",
  "month",
  "year",
];

export const Route = createFileRoute("/dashboard/statistics")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): StatisticsSearch => {
    const period = search.period as OrderStatisticsPeriod;

    return {
      period: PERIOD_KEYS.includes(period) ? period : "today",
    };
  },
});

// Канонический порядок статусов в маршруте заказа — чтобы строки таблицы шли по
// смыслу пути посылки, а не по алфавиту (как их возвращает бэкенд).
const STATUS_ORDER: OrderStatus[] = [
  "created",
  "cancelled",
  "accepted",
  "going_to_sender",
  "decided",
  "submitted",
  "accounted",
  "checked",
  "revision",
  "dispatched",
  "transit",
  "arrived",
  "assigned",
  "delivers_recipient",
  "completed",
];

const statusColorMap: Record<
  OrderStatus,
  "default" | "accent" | "success" | "warning" | "danger"
> = {
  created: "default",
  cancelled: "danger",
  accepted: "accent",
  going_to_sender: "accent",
  decided: "danger",
  submitted: "accent",
  accounted: "warning",
  checked: "warning",
  revision: "warning",
  dispatched: "warning",
  transit: "accent",
  arrived: "accent",
  assigned: "accent",
  delivers_recipient: "accent",
  completed: "success",
};

const PERIODS: { key: OrderStatisticsPeriod; label: string }[] = [
  { key: "today", label: "Сегодня" },
  { key: "threeDays", label: "3 дня" },
  { key: "week", label: "Неделя" },
  { key: "month", label: "Месяц" },
  { key: "year", label: "Год" },
];

function RouteComponent() {
  // Показываем по одному периоду за раз — так бэкенд считает статистику только за
  // выбранное окно и не сканирует всю таблицу заказов на каждый запрос.
  // Выбранный период фиксируем в query params, чтобы ссылку можно было
  // шарить и открывать сразу с нужным фильтром.
  const { period } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isFetching } = useOrdersStatistics({ period });

  const rows = data?.data ?? [];

  // Приводим ответ к словарю status -> счётчик и раскладываем по каноническому
  // порядку. Статусы без заказов всё равно показываем нулями, чтобы таблица была
  // полной и предсказуемой.
  const byStatus = new Map(rows.map((row) => [row.status, row]));

  const ordered = STATUS_ORDER.map(
    (status) => byStatus.get(status) ?? { status, count: 0 },
  );

  const total = ordered.reduce((acc, row) => acc + row.count, 0);

  return (
    <div className="flex flex-col pt-4">
      <div className="flex flex-row gap-2 justify-between h-12 items-center">
        <p className="font-bold text-xl">Статистика заказов по статусам</p>
      </div>

      <div className="flex flex-row flex-wrap gap-2 mt-4">
        {PERIODS.map((item) => (
          <Button
            key={item.key}
            size="sm"
            variant={period === item.key ? "primary" : "ghost"}
            onPress={() => navigate({ search: { period: item.key } })}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {isFetching ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="rounded-2xl bg-neutral-100 p-4 flex flex-col gap-1 mt-4">
            <span className="text-sm text-neutral-500">
              Всего за период «{PERIODS.find((p) => p.key === period)?.label}»
            </span>
            <span className="text-2xl font-bold">
              {total.toLocaleString("ru-RU")}
            </span>
          </div>

          <Table.Root className="mt-6">
            <Table.Content aria-label="Статистика заказов по статусам">
              <Table.Header>
                <Table.Column>Статус</Table.Column>
                <Table.Column>Количество</Table.Column>
              </Table.Header>
              <Table.Body items={ordered}>
                {(row: OrderStatusStatistic) => (
                  <Table.Row id={row.status}>
                    <Table.Cell>
                      <Chip
                        color={statusColorMap[row.status] ?? "default"}
                        size="sm"
                        variant="soft"
                      >
                        {orderTools.status.toString(row.status)}
                      </Chip>
                    </Table.Cell>
                    <Table.Cell className="font-mono text-sm tabular-nums">
                      {row.count.toLocaleString("ru-RU")}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Content>
          </Table.Root>
        </>
      )}
    </div>
  );
}
